import {
	Account,
	NumberServiceResponse,
	Post,
	StripePaymentResponse,
	User,
	UserLedger,
	convertFromLocalTimezone
} from 'model';
import { BaseApi } from './BaseAPI';
import { installationTZ } from './PostApi.js';
const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const baseURL = 'userLedger';

export interface StripeSessionData extends Record<string, unknown> {
	url: string;
	status: string;
}

export const UserLedgerAPI = BaseApi.injectEndpoints({
	endpoints: builder => ({
		getAdminLedger: builder.query<UserLedger[], User['_id']>({
			query: user => ({
				url: `${baseURL}/user/${user}`
			}),
			providesTags: () => [{ type: 'User Ledger' }]
		}),

		getOwnLedger: builder.query<UserLedger, undefined>({
			query: () => ({
				url: `${baseURL}/user/`
			}),
			providesTags: () => [{ type: 'User Ledger' }]
		}),

		getOwnLedgerBalance: builder.query<NumberServiceResponse, undefined>({
			query: () => ({
				url: `${baseURL}/balance/`
			}),
			providesTags: () => [{ type: 'User Ledger' }]
		}),

		getAdminLedgerBalance: builder.query<NumberServiceResponse, User['_id']>({
			query: user => ({
				url: `${baseURL}/balance/${user}`
			}),
			providesTags: () => [{ type: 'User Ledger' }]
		}),

		createLedger: builder.mutation<UserLedger, { ledger: UserLedger }>({
			query: ({ ledger }) => ({
				url: `${baseURL}`,
				method: 'PUT',
				body: { ...ledger }
			}),
			invalidatesTags: () => [{ type: 'User Ledger' }]
		}),

		deleteLedger: builder.mutation<UserLedger, UserLedger['_id']>({
			query: ledger => ({
				url: `${baseURL}/${ledger}`,
				method: 'DELETE'
			}),
			invalidatesTags: () => [{ type: 'User Ledger' }]
		}),

		unDeleteLedger: builder.mutation<UserLedger, UserLedger['_id']>({
			query: ledger => ({
				url: `${baseURL}/${ledger}`,
				method: 'POST'
			}),
			invalidatesTags: () => [{ type: 'User Ledger' }]
		}),

		purchasePromotion: builder.mutation<UserLedger, { postId: Post['_id']; startDate: string; endDate: string }>({
			query: ({ postId, startDate, endDate }) => {
				// We need to adjust to UTC as any dates selected locally are local dates.
				startDate = convertFromLocalTimezone(startDate, localTZ, installationTZ);
				endDate = convertFromLocalTimezone(endDate, localTZ, installationTZ);
				return {
					url: `${baseURL}/purchasePromotion/${postId}/${startDate}/${endDate}`,
					method: 'PUT'
				};
			},
			invalidatesTags: () => [{ type: 'User Ledger' }, { type: 'Post' }]
		}),

		startStripeSession: builder.query<StripePaymentResponse, { quantity: number; type: Account }>({
			query: ({ quantity, type }: { quantity: number; type: Account }) => ({
				url: `payments/stripe/session/create/${type}/${quantity}`,
				method: 'POST',
				body: { quantity }
			})
		}),
		getStripeSession: builder.query<StripeSessionData, string>({
			query: sessionid => ({
				url: `payments/stripe/session/${sessionid}`,
				method: 'GET'
			})
		}),
		updateStripeSuccess: builder.mutation<UserLedger, StripePaymentResponse>({
			query: paymentSession => ({
				url: `payments/stripe/success/`,
				method: 'PUT',
				body: paymentSession
			})
		})
	})
});

export const {
	useGetAdminLedgerQuery,
	useGetOwnLedgerQuery,
	useGetOwnLedgerBalanceQuery,
	useGetAdminLedgerBalanceQuery,
	useCreateLedgerMutation,
	useDeleteLedgerMutation,
	useUnDeleteLedgerMutation,
	usePurchasePromotionMutation,
	useLazyStartStripeSessionQuery,
	useGetStripeSessionQuery,
	useUpdateStripeSuccessMutation
} = UserLedgerAPI;
